import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface LostReasonModalProps {
  isOpen: boolean;
  businessId: string;
  onConfirm: (reason: string) => void;
  onSkip: () => void;
  onCancel: () => void;
}

interface LostReason {
  id: string;
  reason: string;
}

export default function LostReasonModal({ isOpen, businessId, onConfirm, onSkip, onCancel }: LostReasonModalProps) {
  const [reasons, setReasons] = useState<LostReason[]>([]);
  const [selectedReason, setSelectedReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newReason, setNewReason] = useState('');

  useEffect(() => {
    if (isOpen && businessId) {
      setSelectedReason('');
      setNewReason('');
      setIsAdding(false);
      fetchReasons();
    }
  }, [isOpen, businessId]);

  const fetchReasons = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('crm_lost_reasons')
        .select('id, reason')
        .eq('business_id', businessId)
        .order('reason');

      if (error) throw error;
      setReasons(data || []);
    } catch (err) {
      console.error('Error fetching lost reasons:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddReason = async () => {
    if (!newReason.trim() || !businessId) return;
    setIsAdding(true);
    try {
      const { data, error } = await supabase
        .from('crm_lost_reasons')
        .insert({ business_id: businessId, reason: newReason.trim() })
        .select('id, reason')
        .single();

      if (error) {
        if (error.code === '23505') {
          toast.error('This reason already exists');
        } else {
          throw error;
        }
        return;
      }

      const updated = [...reasons, data].sort((a, b) => a.reason.localeCompare(b.reason));
      setReasons(updated);
      setSelectedReason(data.reason);
      setNewReason('');
    } catch (err) {
      console.error('Error adding lost reason:', err);
      toast.error('Failed to add reason');
    } finally {
      setIsAdding(false);
    }
  };

  const handleConfirm = () => {
    if (selectedReason) {
      onConfirm(selectedReason);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    // z-100100: must clear the CRM opportunity drawer (z-100020) and the other
    // modal stacks (~100000–100060) — it can open on top of any of them
    // (e.g. Messages → lead banner → opportunity → mark as lost).
    <div
      className="fixed inset-0 z-[100100] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="fixed inset-0 bg-black/40" />

      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-[fadeIn_0.15s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="h-4.5 w-4.5 text-red-500" />
            </div>
            <div>
              <h3 className="text-[17px] font-semibold text-gray-900">Lost Reason</h3>
              <p className="text-[13px] text-gray-500">Why was this lead lost?</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 pb-4 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Dropdown (always shown) */}
              <select
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
                className="w-full px-4 py-3 text-[15px] border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                style={{ minHeight: '48px' }}
              >
                <option value="">{reasons.length === 0 ? 'No reasons yet — add one below' : 'Select a reason...'}</option>
                {reasons.map((r) => (
                  <option key={r.id} value={r.reason}>{r.reason}</option>
                ))}
              </select>

              {/* Inline add reason */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddReason(); }}
                  placeholder="Add new reason..."
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
                <button
                  onClick={handleAddReason}
                  disabled={!newReason.trim() || isAdding}
                  className={`p-2.5 rounded-xl transition-colors touch-manipulation ${
                    newReason.trim() && !isAdding
                      ? 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2.5 px-5 pb-5">
          <button
            onClick={onSkip}
            className="flex-1 px-4 py-3 text-[15px] font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation"
          >
            Skip
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedReason}
            className={`flex-1 px-4 py-3 text-[15px] font-medium rounded-xl transition-colors touch-manipulation ${
              selectedReason
                ? 'text-white bg-red-500 hover:bg-red-600 active:bg-red-700'
                : 'text-gray-400 bg-gray-100 cursor-not-allowed'
            }`}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
